import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import {
  Box,
  Typography,
  TextField,
  Button,
  Checkbox,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import BookStepsLayout from '../../app/common/BookStepsLayout';
import PompaProgressBar from '../PompaProgressBar/PompaProgressBar';
import { getApiKey } from '../../app/api/agent';
import { initializePixel } from '../../utils/metaPixel';
import useCookies from '../../hooks/useCookies';
import useSetFbcCookie from '../../hooks/setFbcCookie';
import useSentryEvent from '../../hooks/useSentryEvent';
import { getAmount } from './helpers';
import getDomain from '../../utils/getDomain';

const TermsSignDefault = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isConfirmed, setIsConfirmed] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { getCookie, getCookies, setCookie } = useCookies();
  const setFbcCookie = useSetFbcCookie();
  const [purchaseFlag, setPurchaseFlag] = useState(false);
  const { sendSentryEvent } = useSentryEvent();
  const [dealAmount, setDealAmount] = useState();
  const [dealId, setDealId] = useState(null);

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    address: '',
    city: '',
    state: '',
    zip: '',
    phone: '',
    email: '',
    contactId: '',
  });

  const getPixelCookies = () => {
    return getCookies([
      '_fbc',
      '_fbp',
      'user_agent',
      'handl_ip',
      'gclid',
      'handl_url',
      'handlID',
      'first_handlID',
      'handlID_ref',
      'utm_cart',
      'fb_pixel_id',
    ]);
  };

  const [errors, setErrors] = useState({});

  useEffect(() => {
    initializePixel('9915436905226131');
  }, []);

  useEffect(() => {
    const handleTokenVerification = async () => {
      try {
        // Read token from URL query parameter
        const token = searchParams.get('token');

        if (!token) {
          setError('Token is required');
          setLoading(false);
          return;
        }

        // Call token verification API
        const verifyResponse = await axios.post(
          'https://api.pompaprogram.com/verify-token',
          { token: token },
          {
            headers: {
              'x-api-key': getApiKey(),
              'Content-Type': 'application/json',
            },
          }
        );

        // Set FBC cookie from verify-token response
        setFbcCookie(verifyResponse.data.user);

        const { user, dealId } = verifyResponse.data;
        setDealId(dealId);
        // Call second API to get contact data
        const contactResponse = await axios.post(
          'https://api.pompaprogram.com/get-term-sign-link',
          {
            email: user.email,
            dealId: dealId,
          },
          {
            headers: {
              'Content-Type': 'application/json',
            },
          }
        );

        const { contact, user: dynamoDbUser, deal } = contactResponse.data;
        setDealAmount(deal.properties.amount);
        setPurchaseFlag(dynamoDbUser.purchaseFlag);
        // Populate form with contact data
        const _fbcCookie = getCookie('_fbc');
        if (!_fbcCookie && contact.properties.fbc) {
          console.log('Setting FBC cookie from HubSpot');
          setCookie('_fbc', contact.properties.fbc, {
            expires: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000),
            path: '/',
            domain: getDomain(),
          });
        }
        setFormData({
          firstName: contact.properties.firstname || '',
          lastName: contact.properties.lastname || '',
          address: contact.properties.address || '',
          city: contact.properties.city || '',
          state: contact.properties.state || '',
          zip: contact.properties.zip || '',
          phone: contact.properties.phone || '',
          email: dynamoDbUser.email || '',
          contactId: contact.id,
        });

        setLoading(false);
      } catch (error) {
        console.error('Error during verification process:', error);
        setError('Error verifying token or loading contact data');
        setLoading(false);
      }
    };

    handleTokenVerification();
  }, [searchParams]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (value.trim()) setErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = {};

    // Validate form fields
    if (!formData.firstName.trim())
      newErrors.firstName = 'First Name is required';
    if (!formData.lastName.trim()) newErrors.lastName = 'Last Name is required';
    if (!formData.address.trim()) newErrors.address = 'Address is required';
    if (!formData.city.trim()) newErrors.city = 'City is required';
    if (!formData.state.trim()) newErrors.state = 'State is required';
    if (!formData.zip.trim()) newErrors.zip = 'Zip is required';
    if (!formData.phone.trim()) newErrors.phone = 'Phone is required';

    if (Object.keys(newErrors).length) {
      setErrors(newErrors);
      return;
    }

    setIsSubmitting(true);
    const handl_event_id = getCookie('first_handlID');
    const eventId = handl_event_id ? 'pp_bp_' + handl_event_id : 'pp_bp_';
    const eventName = 'Purchase';
    const amount = getAmount(dealAmount);
    const _fbcCookie = getCookie('_fbc');
    try {
      if (!purchaseFlag && amount > 0 && _fbcCookie) {
        console.log(
          'Firing Client Side Purchase event success for customer',
          formData.email
        );
        sendSentryEvent(
          `Firing Client Side Purchase event success for customer ${formData.email} with eventId ${eventId} amount ${amount}`
        );
        // if (handl_event_id) {
        // window.HandL.sendFBEvent(
        //   eventName,
        //   { value: amount, currency: 'USD' },
        //   { eventID: eventId }
        // );
        // } else {
        // window.HandL.sendFBEvent(eventName, {
        //   value: amount,
        //   currency: 'USD',
        // });
        // }
      }

      await axios.post(
        `https://api.pompaprogram.com/submit-new-terms`,
        {
          email: formData.email,
          address: formData.address,
          city: formData.city,
          state: formData.state,
          zip: formData.zip,
          firstname: formData.firstName,
          lastname: formData.lastName,
          phone: formData.phone,
          contactId: parseInt(formData.contactId, 10),
          ...getPixelCookies(),
          internalPixelId: 33,
          eventName,
          eventId,
          event_value: amount,
          dealId: dealId,
        },
        {
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      setMessage('Form submitted successfully');
      setIsModalOpen(true);
    } catch (error) {
      sendSentryEvent(
        `Firing Client Side Purchase event error for customer ${formData.email} with handl_event_id ${handl_event_id} and error ${error}`
      );
      console.error('Submit error:', error);
      setIsSubmitting(false);
      setMessage('Error submitting form');
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    navigate('/');
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          width: '100%',
          minHeight: '100vh',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#e0f2fe',
          p: 2,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center',
            backgroundColor: 'background.paper',
            p: 4,
            borderRadius: 2,
            boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
            width: '100%',
            maxWidth: 700,
          }}
        >
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 2 }}>
            LOADING...
          </Typography>
          <PompaProgressBar width="55%" message="Loading..." />
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          display: 'flex',
          width: '100%',
          minHeight: '100vh',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#e0f2fe',
          p: 2,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center',
            backgroundColor: 'background.paper',
            p: 4,
            borderRadius: 2,
            boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
            width: '100%',
            maxWidth: 700,
          }}
        >
          <Typography
            variant="h4"
            sx={{ fontWeight: 700, mb: 2, color: 'error.main' }}
          >
            ERROR
          </Typography>
          <Typography variant="body1" sx={{ color: 'error.main' }}>
            {error}
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <BookStepsLayout>
      <Box sx={{ maxWidth: 700, mx: 'auto', py: 4 }}>
        <Typography
          variant="h4"
          sx={{
            fontSize: '32px',
            fontWeight: '700',
            textAlign: 'center',
            mb: 3,
          }}
        >
          Address Verification
        </Typography>
        <Box
          component="form"
          noValidate
          onSubmit={handleSubmit}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 3,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              width: '100%',
              maxWidth: 400,
            }}
          >
            <Button
              variant="contained"
              type="button"
              sx={{
                mt: 2,
                mb: 3,
                backgroundColor: '#5ec074',
                width: '300px',
                height: '48px',
                fontSize: '1.1rem',
                fontWeight: 'bold',
                margin: '20px auto',
                display: 'block',
                '&:hover': {
                  backgroundColor: '#218838',
                },
              }}
              disabled={isSubmitting}
              onClick={handleSubmit}
            >
              My information is Correct
            </Button>
            <Typography
              variant="h6"
              sx={{ fontWeight: 700, textAlign: 'center' }}
            >
              Primary Purchase
            </Typography>
            <TextField
              label="First Name"
              name="firstName"
              value={formData.firstName}
              onChange={handleChange}
              error={!!errors.firstName}
              helperText={errors.firstName}
              fullWidth
            />
            <TextField
              label="Last Name"
              name="lastName"
              value={formData.lastName}
              onChange={handleChange}
              error={!!errors.lastName}
              helperText={errors.lastName}
              fullWidth
            />
            <TextField
              label="Address"
              name="address"
              value={formData.address}
              onChange={handleChange}
              error={!!errors.address}
              helperText={errors.address}
              fullWidth
            />
            <TextField
              label="City"
              name="city"
              value={formData.city}
              onChange={handleChange}
              error={!!errors.city}
              helperText={errors.city}
              fullWidth
            />
            <TextField
              label="State"
              name="state"
              value={formData.state}
              onChange={handleChange}
              error={!!errors.state}
              helperText={errors.state}
              fullWidth
            />
            <TextField
              label="Zip"
              name="zip"
              value={formData.zip}
              onChange={handleChange}
              error={!!errors.zip}
              helperText={errors.zip}
              fullWidth
            />
            <TextField
              label="Phone"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              error={!!errors.phone}
              helperText={errors.phone}
              fullWidth
            />
          </Box>
          <FormControlLabel
            control={
              <Checkbox
                checked={isConfirmed}
                onChange={(e) => setIsConfirmed(e.target.checked)}
              />
            }
            label="I confirm this information is correct"
          />
          {message && (
            <Typography
              variant="body1"
              sx={{ mt: 2, color: 'success.main', textAlign: 'center' }}
            >
              {message}
            </Typography>
          )}
          <Button
            variant="contained"
            type="submit"
            sx={{ mt: 2 }}
            disabled={!isConfirmed || isSubmitting}
          >
            Submit
          </Button>
        </Box>
      </Box>
      <Dialog
        open={isModalOpen}
        onClose={handleCloseModal}
        PaperProps={{
          sx: { backgroundColor: '#e0f2fe', color: '#000' },
        }}
      >
        <DialogTitle sx={{ backgroundColor: '#e0f2fe', color: 'black' }}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            Submission Successful
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ backgroundColor: 'white', color: '#000' }}>
          <Typography>Your form has been submitted successfully.</Typography>
        </DialogContent>
        <DialogActions sx={{ backgroundColor: 'white' }}>
          <Button
            onClick={handleCloseModal}
            sx={{
              backgroundColor: '#79a1b5',
              color: '#fff',
              '&:hover': { backgroundColor: '#28a745' },
            }}
            variant="contained"
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </BookStepsLayout>
  );
};

export default TermsSignDefault;
